<template>
  <app-page labelledby="warehouse-operation-title">
    <page-header title-id="warehouse-operation-title" :title="definition.title" :description="definition.description" />

    <app-state v-if="pageState !== 'ready'" :state="pageState" :title="stateTitle" :message="pageError" @retry="loadData" />
    <div v-else class="warehouse-operation-page">
      <q-banner v-if="operationError" rounded class="bg-negative text-white q-mb-md" data-testid="warehouse-operation-error" role="alert">{{ operationError }}</q-banner>

      <warehouse-operation-form
        :kind="kind"
        :definition="definition"
        :form="form"
        :pending="actionState === 'saving'"
        :warehouse-options="warehouseOptions"
        :nomenclature-options="nomenclatureOptions"
        :account-options="accountOptions"
        :inventory-account-options="inventoryOptions"
        @update:form="form = $event"
        @change="clearResultState"
        @submit="submit"
      >
        <page-section v-if="result" title="Результат проведения" description="Операция проведена и не будет отправлена повторно при обновлении страницы." data-testid="warehouse-operation-result">
          <div class="warehouse-operation-result">
            <status-badge tone="success" icon="check_circle" label="Проведено" />
            <span v-if="result.documentId">Документ №{{ result.documentId }}</span>
            <span v-if="result.transferId">Проводка №{{ result.transferId }}</span>
            <span v-if="result.delta !== undefined">Расхождение: {{ result.delta }}</span>
            <span v-if="result.amount">Сумма: {{ formatMoney(result.amount) }}</span>
            <q-btn flat no-caps color="primary" label="Открыть журнал движений" :to="{ name: 'store_movements' }" data-testid="warehouse-operation-audit-link" />
          </div>
        </page-section>

        <sticky-action-bar
          :state="actionState"
          :dirty="canSubmit"
          primary-label="Провести"
          :primary-test-id="definition.submitTestId"
          :error-message="operationError || definition.failure"
          :show-cancel="false"
          @primary="submit"
        />
      </warehouse-operation-form>
    </div>
  </app-page>
</template>

<script>
import { formatMoney } from '../../services/moneyFormat'
import { AppPage, AppState, PageHeader, PageSection, StatusBadge, StickyActionBar } from '../../components/ui'
import WarehouseOperationForm from './components/WarehouseOperationForm.vue'
import { nomenclatureOption, normalizeNomenclatureRows, normalizeWarehouseRows, warehouseOption } from '../../services/warehouseBrowse'
import {
  applyOperationDefaults,
  billOptions,
  defaultWarehouseSettings,
  emptyOperationForm,
  flattenBills,
  inventoryAccountOptions,
  normalizeWarehouseSettings,
  operationDefinitions,
  operationIsValid,
  operationPayload,
  operationResult,
  workspaceError
} from '../../services/warehouseWorkspace'

export default {
  name: 'WarehouseOperationPage',
  components: { AppPage, AppState, PageHeader, PageSection, StatusBadge, StickyActionBar, WarehouseOperationForm },
  props: { kind: { type: String, required: true } },
  data() {
    return {
      actionState: 'idle',
      bills: [],
      canEdit: null,
      eventHandlers: [],
      form: emptyOperationForm(this.kind),
      loadingParts: new Set(['warehouses', 'nomenclature', 'bills', 'settings', 'permissions']),
      nomenclature: [],
      operationError: '',
      pageError: '',
      result: null,
      settings: { ...defaultWarehouseSettings },
      warehouses: []
    }
  },
  computed: {
    definition() { return operationDefinitions[this.kind] },
    pageState() {
      if (this.pageError) return 'error'
      if (this.canEdit === false) return 'access-denied'
      return this.loadingParts.size ? 'loading' : 'ready'
    },
    stateTitle() {
      if (this.canEdit === false) return 'Недостаточно прав'
      return this.loadingParts.size ? 'Загружаем операцию' : 'Операция не загрузилась'
    },
    warehouseOptions() { return this.warehouses.map(warehouseOption) },
    nomenclatureOptions() { return this.nomenclature.map(nomenclatureOption) },
    accountOptions() { return billOptions(this.bills) },
    inventoryOptions() { return inventoryAccountOptions(this.bills) },
    canSubmit() { return this.actionState !== 'saving' && operationIsValid(this.kind, this.form) }
  },
  mounted() {
    this.bindEvents()
    if (this.connection.isAuthenticated) this.loadData()
    else this.bind('connectionReady', this.loadData)
  },
  beforeUnmount() { this.eventHandlers.forEach(([name, handler]) => this.emitter.off(name, handler)) },
  methods: {
    formatMoney,
    bind(name, handler) { const bound = handler.bind(this); this.eventHandlers.push([name, bound]); this.emitter.on(name, bound) },
    bindEvents() {
      this.bind('onListWarehouses', this.onWarehouses)
      this.bind('onListNomenclature', this.onNomenclature)
      this.bind('didReceiveBillsList', this.onBills)
      this.bind('onGetWarehouseSettings', this.onSettings)
      this.bind('didReceoveUserInfoAcrossOrg', this.onPermissions)
      this.bind(this.definition.event, this.onMutation)
    },
    requestId(prefix) { return this.connection.makeRequestId(prefix) },
    loadData() {
      this.pageError = ''
      this.loadingParts = new Set(['warehouses', 'nomenclature', 'bills', 'settings', 'permissions'])
      this.connection.listWarehouses(this.requestId('stock-operation-warehouses'))
      this.connection.listNomenclature(this.requestId('stock-operation-nomenclature'))
      this.connection.getWarehouseSettings(this.requestId('stock-operation-settings'))
      this.connection.getUserInfoAcrossOrg(this.requestId('stock-operation-permissions'))
      const now = new Date()
      this.connection.getBillsList(this.requestId('stock-operation-bills'), Math.floor(new Date(now.getFullYear(), 0, 1).getTime() / 1000), Math.floor(new Date(now.getFullYear(), 11, 31, 23, 59, 59).getTime() / 1000))
    },
    finishPart(name, response, fallback) {
      this.loadingParts.delete(name)
      if (response.type === 'error') this.pageError = workspaceError(response, fallback)
    },
    applyDefaults() { applyOperationDefaults(this.form, this.kind, { warehouses: this.warehouses, nomenclature: this.nomenclature, bills: this.bills, settings: this.settings }) },
    onWarehouses(response) { this.finishPart('warehouses', response, 'Не удалось загрузить склады'); if (response.type !== 'error') this.warehouses = normalizeWarehouseRows(response); this.applyDefaults() },
    onNomenclature(response) { this.finishPart('nomenclature', response, 'Не удалось загрузить номенклатуру'); if (response.type !== 'error') this.nomenclature = normalizeNomenclatureRows(response); this.applyDefaults() },
    onBills(response) { this.finishPart('bills', response, 'Не удалось загрузить счета'); if (response.type !== 'error') this.bills = flattenBills(response.bills || []); this.applyDefaults() },
    onSettings(response) {
      this.finishPart('settings', response, 'Не удалось загрузить настройки склада')
      if (response.type !== 'error') {
        this.settings = normalizeWarehouseSettings(response)
        if (this.kind === 'transfer') this.form.source_warehouse_id = null
        else this.form.warehouse_id = null
        if (this.kind === 'opening') this.form.debit_account_id = null
        else this.form.inventory_account_id = null
        if (this.kind === 'writeOff') this.form.expense_account_id = null
      }
      this.applyDefaults()
    },
    onPermissions(response) {
      this.loadingParts.delete('permissions')
      const permissions = response.user && response.user.role && response.user.role.permissions
      this.canEdit = Boolean(permissions && permissions.edit_stock === true)
    },
    clearResultState() {
      if (this.actionState === 'error' || this.actionState === 'saved') this.actionState = 'idle'
      this.operationError = ''
      this.result = null
    },
    submit() {
      if (!this.canSubmit || this.actionState === 'saving') return
      this.operationError = ''
      this.result = null
      this.actionState = 'saving'
      const payload = operationPayload(this.kind, this.form, this.nomenclature)
      if (this.kind === 'opening') this.connection.createOpeningTransfer(this.requestId(this.definition.requestPrefix), payload)
      else if (this.kind === 'transfer') this.connection.createWarehouseTransfer(this.requestId(this.definition.requestPrefix), payload)
      else if (this.kind === 'adjustment') this.connection.createInventoryAdjustment(this.requestId(this.definition.requestPrefix), payload)
      else this.connection.createStockWriteOff(this.requestId(this.definition.requestPrefix), payload)
    },
    onMutation(response) {
      if (this.actionState !== 'saving') return
      if (response.type === 'error') {
        this.operationError = workspaceError(response, this.definition.failure)
        this.actionState = 'error'
        return
      }
      this.result = operationResult(response)
      this.actionState = 'saved'
      this.$q.notify({ progress: true, message: this.definition.success, icon: 'check', color: 'white', textColor: 'primary' })
    }
  }
}
</script>

<style scoped>
.warehouse-operation-page { max-width: 56rem; }
.warehouse-operation-result { display: flex; flex-wrap: wrap; align-items: center; gap: var(--sid-space-3); }
</style>
